import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  TaskEquipmentManualInput,
  TaskEquipmentWarehouseInput,
  TaskEquipmentPlannedInput,
  TaskEquipmentUpdateInput,
  TaskEquipmentAssignAssetInput,
  TaskEquipmentReturnInput,
} from '@models/warehouse.model';
import { warehouseService } from '@services/warehouse';
import { TASK_EQUIPMENTS_QUERY_KEY } from './useTaskEquipments';
import { EQUIPMENT_ASSETS_QUERY_KEY } from './useEquipmentAssets';
import { getErrorMessage } from '@utils/errorMessage';
import { useTranslation } from '@hooks/useTranslation';

/** Extract a user-friendly error message from graphql-request errors */

export const useCreateTaskEquipmentManual = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskEquipment', 'createManual'],
    mutationFn: async (input: TaskEquipmentManualInput) => {
      return await warehouseService.createTaskEquipmentManual(input);
    },
    onSuccess: (_data, variables) => {
      toast.success(tActionMessages.equipmentAddedManually);
      queryClient.invalidateQueries({
        queryKey: [...TASK_EQUIPMENTS_QUERY_KEY, variables.taskId],
      });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to add equipment'));
    },
  });
};

export const useCreateTaskEquipmentFromWarehouse = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskEquipment', 'createFromWarehouse'],
    mutationFn: async (input: TaskEquipmentWarehouseInput) => {
      return await warehouseService.createTaskEquipmentFromWarehouse(input);
    },
    onSuccess: (_data, variables) => {
      toast.success(tActionMessages.equipmentAddedFromWarehouse);
      queryClient.invalidateQueries({
        queryKey: [...TASK_EQUIPMENTS_QUERY_KEY, variables.taskId],
      });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(
        getErrorMessage(error, 'Failed to add equipment from warehouse'),
      );
    },
  });
};

export const useCreateTaskEquipmentPlanned = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskEquipment', 'createPlanned'],
    mutationFn: async (input: TaskEquipmentPlannedInput) => {
      return await warehouseService.createTaskEquipmentPlanned(input);
    },
    onSuccess: (_data, variables) => {
      toast.success(tActionMessages.plannedEquipmentAdded);
      queryClient.invalidateQueries({
        queryKey: [...TASK_EQUIPMENTS_QUERY_KEY, variables.taskId],
      });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to add planned equipment'));
    },
  });
};

export const useUpdateTaskEquipment = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskEquipment', 'update'],
    mutationFn: async (input: TaskEquipmentUpdateInput) => {
      return await warehouseService.updateTaskEquipment(input);
    },
    onSuccess: () => {
      toast.success(tActionMessages.equipmentUpdated);
      queryClient.invalidateQueries({ queryKey: TASK_EQUIPMENTS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update equipment'));
    },
  });
};

export const useAssignTaskEquipmentAsset = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskEquipment', 'assignAsset'],
    mutationFn: async (input: TaskEquipmentAssignAssetInput) => {
      return await warehouseService.assignTaskEquipmentAsset(input);
    },
    onSuccess: () => {
      toast.success(tActionMessages.assetAssignedToEquipment);
      queryClient.invalidateQueries({ queryKey: TASK_EQUIPMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to assign asset'));
    },
  });
};

export const useCheckoutTaskEquipment = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskEquipment', 'checkout'],
    mutationFn: async (taskEquipmentId: string) => {
      return await warehouseService.checkoutTaskEquipment(taskEquipmentId);
    },
    onSuccess: () => {
      toast.success(tActionMessages.equipmentCheckedOut);
      queryClient.invalidateQueries({ queryKey: TASK_EQUIPMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to checkout equipment'));
    },
  });
};

export const useReturnTaskEquipment = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskEquipment', 'return'],
    mutationFn: async (input: TaskEquipmentReturnInput) => {
      return await warehouseService.returnTaskEquipment(input);
    },
    onSuccess: () => {
      toast.success(tActionMessages.equipmentReturned);
      queryClient.invalidateQueries({ queryKey: TASK_EQUIPMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to return equipment'));
    },
  });
};

export const useDeleteTaskEquipment = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['taskEquipment', 'delete'],
    mutationFn: async (taskEquipmentId: string) => {
      return await warehouseService.deleteTaskEquipment(taskEquipmentId);
    },
    onSuccess: () => {
      toast.success(tActionMessages.equipmentRemoved);
      queryClient.invalidateQueries({ queryKey: TASK_EQUIPMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_ASSETS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to remove equipment'));
    },
  });
};
